import * as Yup from "yup";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { DEFAULT_CURRENCY_CODE } from "../constants/defaults";
import { isPrepaymentTransactionType } from "../resources/common/isPrepaymentTransactionType";
import { createTransactionTypeFields } from "./transactionTypeProfileValidation";
import {
  DOCUMENT_NOTE_MAX_LENGTH,
  DOCUMENT_NOTE_SPECIAL_CHAR_MESSAGE,
  isAllowedDocumentNote,
} from "../resources/common/DocumentNoteField";

dayjs.extend(isSameOrAfter);

const normalizeString = (val) => (typeof val === "string" ? val.trim() : val);

const hasAnyPrepaymentValue = (values) => {
  const prepaidAmount =
    values?.prepaid_amount == null || values?.prepaid_amount === ""
      ? null
      : Number(values.prepaid_amount);

  const ref = values?.prepayment_invoice_reference || {};
  const invoiceNumber = normalizeString(ref.invoice_number) || "";
  const invoiceUuid = normalizeString(ref.invoice_uuid) || "";
  const invoiceDate = normalizeString(ref.invoice_date) || "";

  // "any one filled" should trigger full requirement
  return (
    (prepaidAmount != null && !Number.isNaN(prepaidAmount)) ||
    invoiceNumber.length > 0 ||
    invoiceUuid.length > 0 ||
    invoiceDate.length > 0
  );
};

const requiresPrepaymentFields = (values) =>
  isPrepaymentTransactionType(values?.transaction_type) ||
  hasAnyPrepaymentValue(values);

export const InvoiceValidationSchema = Yup.object({
  ...createTransactionTypeFields(),

  document_number: Yup.string().when("$auto_numbering_enabled", {
    is: false,
    then: (schema) =>
      schema
        .required("Document number is required")
        .test("log-debug", "DEBUG", function (value) {
          return true;
        }),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),

  issue_date: Yup.date().required("Invoice date is required"),

  payment_means: Yup.mixed().when(
    "profile_execution_id",
    ([profileExecutionId], schema) => {
      return Yup.mixed().nullable().notRequired();
    },
  ),
  accounting_supplier_party: Yup.mixed().required("Billed by is required"),

  accounting_customer_party: Yup.mixed().required("Billed to is required"),

  document_lines: Yup.array()
    .of(Yup.object().shape({}))
    .ensure()
    .min(1, "At least one line item is required")
    .required("Line items are required"),

  document_currency_code: Yup.string()
    .transform((val) => (typeof val === "string" ? val.trim() : val))
    .required("Currency code is required"),

  tax_exchange_rate: Yup.object({
    calculation_rate: Yup.number()
      .nullable()
      .transform((_, val) => (val === "" ? null : Number(val)))
      .when("document_currency_code", {
        is: (code) => code !== DEFAULT_CURRENCY_CODE,
        then: (schema) =>
          schema
            .required("Must be a valid number")
            .moreThan(0, "Must be greater than 0"),
        otherwise: (schema) => schema.notRequired(),
      }),
  }).nullable(),

  charges: Yup.array().of(Yup.object().shape({})).ensure().notRequired(),

  allowances: Yup.array().of(Yup.object().shape({})).ensure().notRequired(),

  // Conditional validation: seller_supplier_party.id required when position 6 is "1"

  principle_id_trn: Yup.string().when(
    "profile_execution_id",
    ([profileExecutionId], schema) => {
      return schema.notRequired().nullable();
    },
  ),

  // Conditional validation: invoice_period required when Summary (position 5 / index 4) is "1"
  invoice_period_start_date: Yup.date()
    .nullable()
    .when("transaction_type", {
      is: (transactionType) =>
        typeof transactionType === "string" &&
        transactionType.length === 20 &&
        (transactionType.charAt(4) === "1" ||
          transactionType.charAt(5) === "1"),
      then: (schema) =>
        schema.required(
          "Invoicing period start date is required for Summary Invoice",
        ),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  invoice_period_end_date: Yup.date()
    .nullable()
    .when("transaction_type", {
      is: (transactionType) =>
        typeof transactionType === "string" &&
        transactionType.length === 20 &&
        (transactionType.charAt(4) === "1" ||
          transactionType.charAt(5) === "1"),
      then: (schema) =>
        schema.required(
          "Invoicing period end date is required for Summary Invoice",
        ),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  invoice_period_description: Yup.string()
    .nullable()
    .when("transaction_type", {
      is: (transactionType) =>
        typeof transactionType === "string" &&
        transactionType.length === 20 &&
        transactionType.charAt(4) === "1",
      then: (schema) =>
        schema.required("Frequency of billing is required for Summary Invoice"),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

  note: Yup.string()
    .nullable()
    .max(
      DOCUMENT_NOTE_MAX_LENGTH,
      `Note must be at most ${DOCUMENT_NOTE_MAX_LENGTH} characters`,
    )
    .test(
      "no-special-characters",
      DOCUMENT_NOTE_SPECIAL_CHAR_MESSAGE,
      isAllowedDocumentNote,
    )
    .when("invoice_period_description", {
      is: (description) => {
        if (typeof description !== "string") {
          return false;
        }

        const normalized = description.trim().toUpperCase();

        return normalized === "OTHERS" || normalized === "OTH";
      },
      then: (schema) =>
        schema.required(
          "Invoice note is mandatory when frequency of billing is Others",
        ),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  prepaid_amount: Yup.number()
    .nullable()
    .transform((_, val) => (val === "" || val == null ? null : Number(val)))
    .min(0, "Amount cannot be negative")
    .test(
      "prepaid-amount-required-for-prepayment",
      "Prepayment details are required",
      function (value) {
        if (!isPrepaymentTransactionType(this.parent?.transaction_type)) {
          return true;
        }

        return (
          value != null && !Number.isNaN(Number(value)) && Number(value) > 0
        );
      },
    ),
  prepayment_invoice_reference: Yup.object({
    invoice_number: Yup.string()
      .transform((val) => (typeof val === "string" ? val.trim() : val))
      .nullable(),
    invoice_uuid: Yup.string()
      .transform((val) => (typeof val === "string" ? val.trim() : val))
      .nullable(),
    invoice_date: Yup.string()
      .transform((val) => (typeof val === "string" ? val.trim() : val))
      .nullable(),
  })
    .nullable()
    .default(null),
}).test(
  "prepayment-all-fields-mandatory",
  "Prepayment details are required",
  function (values) {
    if (!requiresPrepaymentFields(values)) return true;

    const prepaidAmount =
      values?.prepaid_amount == null || values?.prepaid_amount === ""
        ? null
        : Number(values.prepaid_amount);
    const ref = values?.prepayment_invoice_reference || {};
    const invoiceNumber = normalizeString(ref.invoice_number) || "";
    const invoiceUuid = normalizeString(ref.invoice_uuid) || "";

    const amountOk =
      prepaidAmount != null &&
      !Number.isNaN(prepaidAmount) &&
      prepaidAmount > 0;
    const numberOk = invoiceNumber.length > 0;
    const uuidOk = invoiceUuid.length > 0;

    // Prefer a single button-level message when Prepayment is selected and nothing is filled
    if (
      isPrepaymentTransactionType(values?.transaction_type) &&
      !amountOk &&
      !numberOk &&
      !uuidOk
    ) {
      return this.createError({
        path: "prepaid_amount",
        message: "Prepayment details are required",
      });
    }

    if (!amountOk) {
      return this.createError({
        path: "prepaid_amount",
        message: "Prepaid Amount is required and must be greater than 0",
      });
    }
    if (!numberOk) {
      return this.createError({
        path: "prepayment_invoice_reference.invoice_number",
        message: "Invoice Number is required",
      });
    }
    if (!uuidOk) {
      return this.createError({
        path: "prepayment_invoice_reference.invoice_uuid",
        message: "Invoice UUID is required",
      });
    }

    return true;
  },
);

export default InvoiceValidationSchema;
